import type { StateCreator } from 'zustand';
import type { Leverage, StockSymbol } from '../../types';
import { BROKE_LINE, CHART_LEN, LEV_NAMES, STOCKS, UP_BIAS } from '../../constants';
import { persist } from '../persist';
import type { GameState } from '../types';
import { haptic } from '../../sdk/haptic';

type Prices = Record<StockSymbol, number[]>;

export interface LiqInfo {
  stockName: string;
  levName: string;
  lev: number;
  totalLoss: number;
  gapLoss: number;
}

export interface MarketSlice {
  sym: StockSymbol;
  lev: Leverage;
  prices: Prices;
  tickPaused: boolean;
  liqInfo: LiqInfo | null;

  tick: () => void;
  selectStock: (sym: StockSymbol) => void;
  selectLev: (lev: Leverage) => void;
  fireLiquidation: () => void;
}

const initialPrices: Prices = { CHJ: [10000], BDS: [10000], HYB: [10000] };

export const createMarketSlice: StateCreator<GameState, [], [], MarketSlice> = (set, get) => ({
  sym: 'CHJ',
  lev: 1,
  prices: initialPrices,
  tickPaused: false,
  liqInfo: null,

  tick: () => {
    const s = get();
    if (s.tickPaused) return;

    // 가격 갱신 (50.05:49.95 우상향)
    const newPrices: Prices = {
      CHJ: [...s.prices.CHJ],
      BDS: [...s.prices.BDS],
      HYB: [...s.prices.HYB],
    };
    for (const sym of Object.keys(STOCKS) as StockSymbol[]) {
      const { dMin, dMax } = STOCKS[sym];
      const d = dMin + Math.random() * (dMax - dMin);
      const u = d / (1 - d);
      const pct = Math.random() < UP_BIAS ? u : -d;
      const last = newPrices[sym][newPrices[sym].length - 1];
      newPrices[sym].push(Math.max(last * (1 + pct), 1));
      if (newPrices[sym].length > CHART_LEN + 1) newPrices[sym].shift();
    }

    set({
      prices: newPrices,
      stats: { ...s.stats, totalPlaySec: s.stats.totalPlaySec + 1 },
    });

    // 청산 체크
    const after = get();
    if (after.position) {
      const cur = after.prices[after.position.symbol].at(-1)!;
      const rawPct = (cur - after.position.entryPrice) / after.position.entryPrice;
      const levPct = rawPct * after.position.leverage;
      const posVal = after.position.amount * (1 + levPct);
      if (after.cash + posVal <= 0) get().fireLiquidation();
    }

    // brokeFired 자동 리셋 / 발동
    const final = get();
    if (!final.position) {
      if (final.brokeFired && final.cash >= BROKE_LINE) {
        set({ brokeFired: false });
      } else if (!final.brokeFired && final.cash >= 0 && final.cash < BROKE_LINE) {
        set({ brokeFired: true });
        setTimeout(() => set({ showReviveModal: true }), 300);
      }
    }
    persist(get());
  },

  selectStock: (sym) => {
    const s = get();
    if (s.position) return;
    if (s.tutStep >= 1 && s.tutStep <= 2) return;
    set({ sym });
  },

  selectLev: (lev) => {
    const s = get();
    if (s.position) return;
    if (s.tutStep >= 1 && s.tutStep <= 2) return;
    set({ lev });
  },

  fireLiquidation: () => {
    const s = get();
    if (!s.position) return;
    const p = s.position;
    const cur = s.prices[p.symbol].at(-1)!;
    const rawPct = (cur - p.entryPrice) / p.entryPrice;
    const actualLossPct = Math.min(rawPct * p.leverage, 0);
    const actualLoss = Math.floor(p.amount * Math.abs(actualLossPct));
    const gapLoss = Math.max(actualLoss - p.amount, 0);
    const totalLoss = p.amount + gapLoss;

    haptic('liquidate');
    set({
      cash: s.cash - gapLoss,
      stats: {
        ...s.stats,
        liquidations: s.stats.liquidations + 1,
        totalTrades: s.stats.totalTrades + 1,
        streak: 0,
      },
      position: null,
      showLiqScreen: true,
      liqInfo: {
        stockName: STOCKS[p.symbol].name,
        levName: LEV_NAMES[p.leverage],
        lev: p.leverage,
        totalLoss,
        gapLoss,
      },
    });
    persist(get());
  },
});
